import React, { createContext, useContext, useEffect, useState } from "react";
import { Country, UserRole } from "../../shared/constants/Constant";
import { useFirestore, useFunctions, useUser } from "reactfire";
import * as firebase from "firebase/app";
import ForumUtil from "../../shared/utils/ForumUtils";
import random from "randomstring";
import { UserPermission } from "../../database/models/UserPermission";

export interface WorkshopProgress {
  workshopId: string;
  status: SubsectionStatus;
  subsectionsProgress: SubsectionProgress[];
}

export interface SubsectionProgress {
  subsectionId: string;
  status: SubsectionStatus;
  watchedVideoIds?: string[];
  isArchived?: boolean;
}

export enum SubsectionStatus {
  DONE = "DONE",
  CURRENT = "CURRENT",
  SKIPPED = "SKIPPED",
}

interface Team {
  id: string;
  name: string;
}

export interface IUserContext {
  fireUser: firebase.User | null;
  country: Country;
  createdAt: firebase.firestore.Timestamp | null;
  displayName: string;
  photoURL: string;
  username: string;
  dob: Date;
  university: string;
  contactNo: string;
  bio: string;
  teamIds: string[];
  teams: Team[];
  memberId: string;
  updatedAt: firebase.firestore.Timestamp | null;
  userRole: UserRole;
  permissions: UserPermission[];
  workshopProgress: WorkshopProgress[];
  forumPassword?: string;
  startLearning(
    workshopId: string,
    onFished: () => void,
    onError: () => void
  ): void;
  startNextSubsection(
    workshopId: string,
    startSubsectionId: string,
    nextSubsectionId: string,
    onFinished: () => void,
    onError: () => void
  ): void;
  completeSkippedSubsection(
    workshopId: string,
    subsectionId: string,
    onFinished: () => void,
    onError: () => void
  ): void;
  finishWorkshop(
    workshopId: string,
    currentSubsectionId: string,
    onFinished: () => void,
    onError: () => void
  ): void;
}

interface IState {
  fireUser: firebase.User | null;
  country: Country;
  createdAt: firebase.firestore.Timestamp | null;
  displayName: string;
  photoURL: string;
  username: string;
  dob: Date;
  university: string;
  contactNo: string;
  bio: string;
  teamIds: string[];
  memberId: string;
  updatedAt: firebase.firestore.Timestamp | null;
  userRole: UserRole;
  workshopProgress: WorkshopProgress[];
  teams: Team[];
  forumPassword?: string;
  permissions: UserPermission[];
}

const initialContext: IUserContext = {
  fireUser: null,
  country: Country.SRI_LANKA,
  createdAt: null,
  displayName: "",
  username: "",
  dob: new Date(),
  university: "",
  contactNo: "",
  bio: "",
  teams: [],
  teamIds: [],
  memberId: "",
  photoURL: "",
  updatedAt: null,
  userRole: { isAdmin: false, isModerator: false, isMember: false },
  forumPassword: undefined,
  workshopProgress: [],
  permissions: [],
  startLearning: (
    workshopId: string,
    onFished: () => void,
    onError: () => void
  ) => {},
  startNextSubsection: (
    workshopId: string,
    startSubsectionId: string,
    nextSubsectionId: string,
    onFinished: () => void,
    onError: () => void
  ) => {},
  completeSkippedSubsection: (
    workshopId: string,
    subsectionId: string,
    onFinished: () => void,
    onError: () => void
  ) => {},
  finishWorkshop: (
    workshopId: string,
    currentSubsectionId: string,
    onFinished: () => void,
    onError: () => void
  ) => {},
};

const initialState: IState = {
  fireUser: null,
  country: Country.SRI_LANKA,
  createdAt: null,
  displayName: "",
  photoURL: "",
  username: "",
  dob: new Date(),
  university: "",
  contactNo: "",
  bio: "",
  teamIds: [],
  teams: [],
  memberId: "",
  updatedAt: null,
  userRole: { isAdmin: false, isModerator: false, isMember: false },
  workshopProgress: [],
  permissions: [],
};

const UserContext = createContext<IUserContext>(initialContext);

interface IUserRoleState {
  isAdmin: boolean;
  isModerator: boolean;
  isMember: boolean;
}

const initUserRoleState: IUserRoleState = {
  isAdmin: false,
  isModerator: false,
  isMember: false,
};

interface IProps {
  children: any;
}

export const UserProvider = (props: IProps) => {
  const fireUser: firebase.User = useUser();
  const currentUser: firebase.User | null = firebase.auth().currentUser;
  const store = useFirestore();

  const [state, setState] = useState(initialState);
  const [userRoleState, setUserRoleState] = useState(initUserRoleState);
  const startLearningFunction = useFunctions().httpsCallable(
    "startLearningWorkshop"
  );
  const startNextSubSectionFunction = useFunctions().httpsCallable(
    "startLearningNextSubsection"
  );
  const finshiedWorkshopFunction = useFunctions().httpsCallable(
    "finishLearningWorkshop"
  );
  const finshSkippedSection = useFunctions().httpsCallable(
    "completeSkippedSubsection"
  );
  const saveForumPasswordFunction = useFunctions().httpsCallable(
    "saveForumPassword"
  );

  useEffect(() => {
    currentUser
      ?.getIdTokenResult()
      .then((idTokenResult) => {
        setUserRoleState({
          isAdmin: idTokenResult.claims.isAdmin,
          isModerator: idTokenResult.claims.isModerator,
          isMember: idTokenResult.claims.isMember,
        });
        setState((prevState) => {
          return Object.assign(
            {},
            {
              ...prevState,
              photoURL: currentUser?.photoURL || "",
            }
          );
        });
        // setState({
        //   ...state,
        //   photoURL: currentUser?.photoURL || ""
        // });
      })
      .catch(() => {
        setUserRoleState({
          isAdmin: false,
          isModerator: false,
          isMember: false,
        });
      });
  }, [
    currentUser,
    userRoleState.isMember,
    userRoleState.isModerator,
    userRoleState.isAdmin,
  ]);

  useEffect(() => {
    const unset1 = store
      .collection("users")
      .where("uid", "==", fireUser && fireUser.uid)
      .onSnapshot({}, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added" || change.type === "modified") {
            setState((ps) => {
              const userData: any = change.doc.data();
              const country = userData.country;
              const displayName = userData.displayName;
              const username = userData.username;
              const dob = change.doc.data().dob
                ? change.doc.data().dob instanceof firebase.firestore.Timestamp
                  ? new Date(change.doc.data().dob.seconds * 1000)
                  : new Date(change.doc.data().dob)
                : ps.dob || new Date();
              const university = userData.university || ps.university;
              const contactNo = userData.phoneNumber || ps.contactNo;
              const bio = userData.bio;
              const createdAt = userData.createdAt;
              const updatedAt = userData.updatedAt;
              const userRole = userRoleState;
              const forumPassword = userData.forumPassword;
              const permissions = userData.permissions;

              if (forumPassword) {
                if (!ForumUtil.getForumDataIfAailable()) {
                  ForumUtil.login(
                    {
                      email: fireUser.email || "",
                      password: forumPassword,
                    },
                    () => {
                      console.log("loged uc");
                    },
                    () => {
                      console.log("loged error uc");
                    }
                  );
                }
              } else {
                ForumUtil.register(
                  {
                    displayName: displayName,
                    email: fireUser.email || "",
                    password: random.generate(),
                    profileImage: fireUser.photoURL || "",
                    role: userRole.isAdmin
                      ? "ADMIN"
                      : userRole.isModerator
                      ? "MODERATOR"
                      : "USER",
                  },
                  saveForumPasswordFunction,
                  () => {
                    console.log("registerd uc");
                  },
                  () => {
                    console.log("registerd error uc");
                  }
                );
              }

              return Object.assign(
                {},
                {
                  ...ps,
                  country,
                  displayName,
                  username,
                  dob,
                  university,
                  contactNo,
                  bio,
                  createdAt,
                  updatedAt,
                  userRole,
                  fireUser,
                  forumPassword,
                  permissions,
                }
              );
            });
          }
        });
      });

    const unset2 = store
      .collection("members")
      .where("email", "==", fireUser && fireUser.email)
      .onSnapshot({}, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added" || change.type === "modified") {
            !userRoleState.isModerator &&
              setState((ps) => {
                const memberId: string = change.doc.id;
                const userData: any = change.doc.data();
                const workshopProgress = ((userData.workshopProgress ||
                  []) as WorkshopProgress[]).map((wp) => {
                  return {
                    ...wp,
                    subsectionsProgress:
                      wp.subsectionsProgress?.filter((sp) => !sp.isArchived) ||
                      [],
                  };
                });
                const dob = change.doc.data().dob
                  ? change.doc.data().dob instanceof
                    firebase.firestore.Timestamp
                    ? new Date(change.doc.data().dob.seconds * 1000)
                    : new Date(change.doc.data().dob)
                  : ps.dob || new Date();
                const university = userData.university || "";
                const contactNo = userData.contactNumber
                  ? userData.contactNumber
                  : userData.contact || "";
                const bio = userData.bio || "";
                const teamIds = userData.teamIds || "";

                return Object.assign(
                  {},
                  {
                    ...ps,
                    memberId,
                    dob,
                    university,
                    contactNo,
                    bio,
                    teamIds,
                    workshopProgress,
                  }
                );
              });
          }
        });
      });

    return () => {
      unset1();
      unset2();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fireUser, userRoleState, store]);

  useEffect(() => {
    let unset = () => {};
    if (
      userRoleState &&
      !userRoleState.isModerator &&
      state.teamIds &&
      state.teamIds.length !== 0
    ) {
      unset = store
        .collection("teams")
        .where(firebase.firestore.FieldPath.documentId(), "in", state.teamIds)
        .where("active", "==", true)
        .onSnapshot({}, (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added" || change.type === "modified") {
              setState((ps) => {
                ps.teams.push({
                  id: change.doc.id,
                  name: (change.doc.data() as any).name,
                });
                return Object.assign(
                  {},
                  {
                    ...ps,
                    teams: Object.assign([], ps.teams),
                  }
                );
              });
            }
          });
        });
    }

    return unset;
  }, [state.teamIds, store, userRoleState]);

  const startLearning = (
    workshopId: string,
    onFished: () => void,
    onError: () => void
  ) => {
    startLearningFunction(workshopId)
      .then(onFished)
      .catch(onError);
  };

  const startNextSubsection = (
    workshopId: string,
    startSubsectionId: string,
    nextSubsectionId: string,
    onFinished: () => void,
    onError: () => void
  ) => {
    startNextSubSectionFunction({
      workshopId,
      currentSubsectionId: startSubsectionId,
      nextSubsectionId,
    })
      .then(onFinished)
      .catch(onError);
  };

  const completeSkippedSubsection = (
    workshopId: string,
    subsectionId: string,

    onFinished: () => void,
    onError: () => void
  ) => {
    finshSkippedSection({
      workshopId,
      subsectionId,
    })
      .then(onFinished)
      .catch(onError);
  };

  const finishWorkshop = (
    workshopId: string,
    currentSubsectionId: string,
    onFinished: () => void,
    onError: () => void
  ) => {
    finshiedWorkshopFunction({ workshopId, currentSubsectionId })
      .then(onFinished)
      .catch(onError);
  };
  return (
    <UserContext.Provider
      value={{
        ...state,
        startLearning,
        startNextSubsection,
        finishWorkshop,
        completeSkippedSubsection,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export const useAppUser = (): IUserContext => useContext(UserContext);
